'use client';

/**
 * BannerStack - the ONE region every top-of-page banner renders into.
 *
 *     Andrew, 2026-10-22: "when we get any kind of notification bar across the
 *     top of the page.. right now it overlaps the header. id prefer if they push
 *     down the header and not overlap anything."
 *
 * The region sits in the root layout ABOVE the app shell, in normal document
 * flow, so a banner pushes the header and the page down instead of covering
 * them. Several banners stack (CSS `order`, lowest first). A closed banner
 * unmounts and the space is simply returned.
 *
 * The shell's full-height layouts used to be `h-screen` / `100dvh`; with a bar
 * above them they would overflow by the bar's height. The region therefore
 * publishes its measured height as `--banner-stack-height` on <html> (a
 * ResizeObserver, so an animating banner is followed frame by frame) and
 * `--app-vh` (styles/globals.css) is the viewport minus it. Use `h-[var(--app-vh)]`
 * wherever a layout means "the rest of the screen".
 *
 * Banners are NOT toasts: a transient corner notification (react-hot-toast, the
 * bottom-right stack) is not a top bar and does not belong here.
 */
import { useEffect, useRef, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export const BANNER_STACK_ID = 'banner-stack';
export const BANNER_STACK_HEIGHT_VAR = '--banner-stack-height';

/** Stack order, top to bottom. Gaps leave room for new banners. */
export const BANNER_ORDER = {
  testingMode: 10,
  masquerade: 20,
  tierSimulator: 30,
  authStatus: 40,
  connection: 50,
  callNotice: 60,
  audio: 70,
} as const;

// The host element lives in a tiny external store so a <Banner> anywhere in the
// tree (root layout, template, a call page) finds it without a provider.
let host: HTMLElement | null = null;
const listeners = new Set<() => void>();
function setHost(el: HTMLElement | null): void {
  if (host === el) return;
  host = el;
  listeners.forEach((l) => l());
}
function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}
const getHost = (): HTMLElement | null => host;
const getServerHost = (): HTMLElement | null => null;

export function BannerStack() {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setHost(el);
    const root = document.documentElement;
    const publish = () => root.style.setProperty(BANNER_STACK_HEIGHT_VAR, `${el.getBoundingClientRect().height}px`);
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.setProperty(BANNER_STACK_HEIGHT_VAR, '0px');
      setHost(null);
    };
  }, []);

  return (
    <div
      ref={ref}
      id={BANNER_STACK_ID}
      data-testid="banner-stack"
      className="relative z-[60] flex w-full flex-shrink-0 flex-col"
    />
  );
}

interface BannerProps {
  children: ReactNode;
  /** `alert` for something that needs attention now (a failure), `status` for the rest. */
  role?: 'status' | 'alert';
  /** Position in the stack, lowest first. Use BANNER_ORDER. */
  order?: number;
  testId?: string;
  className?: string;
  /** Extra attributes for the wrapper, e.g. data-mode / aria-label. */
  attrs?: Record<string, string | undefined>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * Renders its children into the BannerStack. Without a stack mounted (a unit
 * test, the first server render) it renders in place, in flow: it still
 * overlaps nothing.
 */
export function Banner({ children, role = 'status', order = BANNER_ORDER.callNotice, testId, className, attrs, onMouseEnter, onMouseLeave }: BannerProps) {
  const target = useSyncExternalStore(subscribe, getHost, getServerHost);
  const style: CSSProperties = { order };
  const node = (
    <div
      role={role}
      aria-live={role === 'alert' ? 'assertive' : 'polite'}
      data-banner=""
      data-testid={testId}
      className={className}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...attrs}
    >
      {children}
    </div>
  );
  return target ? createPortal(node, target) : node;
}
